// frontend/src/pages/LoginPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readAuthIntent, CREATE_LEAGUE_RESUME_PATH } from '@/utils/authIntent';
import { ApiError, googleAuth, GoogleAuthNeedsUsername, appleAuth, AppleAuthNeedsUsername } from '@/api/auth';
import { requestOtp, verifyOtp } from '@/api/auth';
import { mixpanelTrack } from '@/services/mixpanel';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import { signInWithApple } from '@/services/appleSignIn';
import SEO from '@/components/common/SEO';
import { clearReferral, getActiveReferral } from '@/utils/referral';

const normalizePhoneNumberFE = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
  if (cleaned.length === 10) return `+1${cleaned}`;
  return `+${cleaned}`;
};

type PhoneStep = 'idle' | 'code_sent';

const LoginPage = () => {
  const { completeLogin, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = (
    location.state as { authReason?: string } | null
  )?.authReason === 'session_expired';
  // Allowlisted funnel marker (utils/authIntent). Threaded onto the signup
  // toggle and stamped on the funnel events; completeLogin reads the URL
  // itself for post-auth routing.
  const authIntent = readAuthIntent(location.search);
  const intentProps = authIntent ? { 'Intent': authIntent } : {};

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('idle');
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);

  // Google auth state
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [googleNeedsUsername, setGoogleNeedsUsername] = useState<GoogleAuthNeedsUsername | null>(null);
  const [googleIdToken, setGoogleIdToken] = useState<string>('');
  const [googleUsername, setGoogleUsername] = useState('');

  // Apple auth state
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [appleNeedsUsername, setAppleNeedsUsername] = useState<AppleAuthNeedsUsername | null>(null);
  const [appleIdToken, setAppleIdToken] = useState<string>('');
  const [appleAuthCode, setAppleAuthCode] = useState<string>('');
  const [appleUsername, setAppleUsername] = useState('');

  // Own only the initial-session redirect. A login completed on this page is
  // routed by completeLogin, so this effect must not race its intent handling.
  const shouldRedirectExistingSession = useRef(isAuthenticated || isAuthLoading);
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && shouldRedirectExistingSession.current) {
      navigate(authIntent === 'create_league' ? CREATE_LEAGUE_RESUME_PATH : '/home', { replace: true });
    }
    if (!isAuthLoading) shouldRedirectExistingSession.current = false;
  }, [authIntent, isAuthLoading, isAuthenticated, navigate]);

  // Funnel visibility (CREATE-LEAGUE-FUNNEL.md §3.6): only intent-carrying
  // visits are gates; a plain /login visit is ordinary traffic.
  useEffect(() => {
    if (authIntent) mixpanelTrack('Auth Gate Shown', { 'Intent': authIntent, 'Gate': 'login' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleSuccess = async (credential: string) => {
    setError(null);
    setIsLoadingGoogle(true);
    // Google's rendered web button exposes no click hook, so credential
    // receipt is the earliest "started" moment available for this method.
    mixpanelTrack('Auth Method Started', { 'Method': 'google', ...intentProps });
    try {
      const response = await googleAuth(credential);
      if ('needs_username' in response) {
        setGoogleIdToken(credential);
        setGoogleNeedsUsername(response);
        setGoogleUsername(response.suggested_username);
      } else {
        await completeLogin(response.access_token);
        if (response.is_new_user) {
          mixpanelTrack('Signed Up', { 'Signup Method': 'Google', ...intentProps });
        } else {
          mixpanelTrack('Logged In', { 'Login Method': 'Google', ...intentProps });
        }
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Google sign-in failed. Please try again.');
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleGoogleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoadingGoogle(true);
    try {
      const response = await googleAuth(googleIdToken, googleUsername.trim());
      if ('needs_username' in response) {
        setError('That username is taken. Please choose another.');
      } else {
        await completeLogin(response.access_token);
        mixpanelTrack('Signed Up', { 'Signup Method': 'Google', ...intentProps });
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleAppleClick = async () => {
    setError(null);
    setIsLoadingApple(true);
    mixpanelTrack('Auth Method Started', { 'Method': 'apple', ...intentProps });
    try {
      const { idToken, code } = await signInWithApple();
      const response = await appleAuth(idToken, code);
      if ('needs_username' in response) {
        setAppleIdToken(idToken);
        setAppleAuthCode(code);
        setAppleNeedsUsername(response);
        setAppleUsername('');
      } else {
        await completeLogin(response.access_token);
        if (response.is_new_user) {
          mixpanelTrack('Signed Up', { 'Signup Method': 'Apple', ...intentProps });
        } else {
          mixpanelTrack('Logged In', { 'Login Method': 'Apple', ...intentProps });
        }
      }
    } catch (err: any) {
      // Apple's JS SDK rejects with { error: 'popup_closed_by_user' | 'user_cancelled_authorize' | ... }
      // when the user dismisses the popup. Dismissal stays a silent no-op in
      // the UI but is tracked: a dismissed sheet and a never-tapped button were
      // indistinguishable in the 2026-08-10 funnel autopsy.
      const code = err?.error || err?.message || '';
      if (code.includes('popup_closed_by_user') || code.includes('user_cancelled_authorize')) {
        mixpanelTrack('Auth Method Dismissed', { 'Method': 'apple', ...intentProps });
      } else {
        setError(err instanceof ApiError ? err.message : 'Apple sign-in failed. Please try again.');
      }
    } finally {
      setIsLoadingApple(false);
    }
  };

  const handleAppleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoadingApple(true);
    try {
      const response = await appleAuth(appleIdToken, appleAuthCode, appleUsername.trim());
      if ('needs_username' in response) {
        setError('That username is taken. Please choose another.');
      } else {
        await completeLogin(response.access_token);
        mixpanelTrack('Signed Up', { 'Signup Method': 'Apple', ...intentProps });
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoadingApple(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsRateLimited(false);
    setIsLoadingOtp(true);
    const normalizedPhone = normalizePhoneNumberFE(phone);
    if (!normalizedPhone.startsWith('+1') || normalizedPhone.length < 12) {
      setError('Please enter a valid US phone number.');
      setIsLoadingOtp(false);
      return;
    }
    mixpanelTrack('Auth Method Started', { 'Method': 'otp', ...intentProps });
    try {
      await requestOtp(normalizedPhone);
      setPhoneStep('code_sent');
      mixpanelTrack('OTP Requested', { 'Success': true });
    } catch (err: any) {
      const isRL = err instanceof ApiError && err.status === 429;
      setIsRateLimited(isRL);
      setError(err instanceof ApiError ? err.message : 'Failed to send verification code.');
      mixpanelTrack('OTP Request Failed', { 'Error': err?.message, 'Rate Limited': isRL });
    } finally {
      setIsLoadingOtp(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsRateLimited(false);
    setIsLoadingVerify(true);
    const normalizedPhone = normalizePhoneNumberFE(phone);
    const referralSource = getActiveReferral();
    try {
      const loginResponse = await verifyOtp(normalizedPhone, code, referralSource);
      await completeLogin(loginResponse.access_token);
      if (loginResponse.is_new_user) {
        if (referralSource) clearReferral();
        mixpanelTrack('Signed Up', {
          'Signup Method': 'Phone',
          'Referral Source': referralSource || null,
          ...intentProps,
        });
      } else {
        mixpanelTrack('Logged In', { 'Login Method': 'OTP', ...intentProps });
      }
    } catch (err: any) {
      const isRL = err instanceof ApiError && err.status === 429;
      setIsRateLimited(isRL);
      setError(err instanceof ApiError ? err.message : 'Login failed. Please try again.');
      mixpanelTrack('Login Failed', { 'Login Method': 'OTP', 'Error': err?.message, 'Rate Limited': isRL });
    } finally {
      setIsLoadingVerify(false);
    }
  };

  const errorBanner = error && (
    <p className={`mb-4 text-sm text-center rounded p-2 ${isRateLimited ? 'text-amber-400 bg-amber-900/40 border border-amber-500/60' : 'text-secondary bg-red-900/50 border border-secondary'}`}>
      {error}
    </p>
  );

  // --- Apple username selection step ---
  if (appleNeedsUsername) {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <h2 className="text-2xl font-heading font-bold text-accent-light-blue mb-2 text-center">Create a Username</h2>
          <p className="text-sm text-gray-400 text-center mb-6">Pick a username to complete your account</p>
          {errorBanner}
          <form onSubmit={handleAppleUsernameSubmit} className="space-y-3">
            <input
              type="text"
              value={appleUsername}
              onChange={(e) => setAppleUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              placeholder="Choose a username"
              disabled={isLoadingApple}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoadingApple}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingApple ? 'bg-gray-500 text-gray-300 animate-pulse cursor-not-allowed' : 'bg-accent-light-blue hover:bg-blue-400 text-primary'}`}
            >
              {isLoadingApple ? 'Creating Account...' : 'Continue'}
            </button>
            <button type="button" onClick={() => { setAppleNeedsUsername(null); setError(null); }} className="w-full text-xs text-gray-500 hover:text-gray-300 text-center py-1">
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Google username selection step ---
  if (googleNeedsUsername) {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Fantasy Futbol" className="w-28 h-28 object-contain" />
          </div>
          <h2 className="text-xl font-heading font-bold text-accent-light-blue mb-2 text-center">One last step</h2>
          <p className="text-sm text-gray-400 text-center mb-6">Choose a username to complete your account</p>
          {errorBanner}
          <form onSubmit={handleGoogleUsernameSubmit} className="space-y-3">
            <input
              type="text"
              value={googleUsername}
              onChange={(e) => setGoogleUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              placeholder="Choose a username"
              disabled={isLoadingGoogle}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoadingGoogle}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingGoogle ? 'bg-gray-500 text-gray-300 animate-pulse cursor-not-allowed' : 'bg-accent-light-blue hover:bg-blue-400 text-primary'}`}
            >
              {isLoadingGoogle ? 'Creating Account...' : 'Continue'}
            </button>
            <button type="button" onClick={() => { setGoogleNeedsUsername(null); setError(null); }} className="w-full text-xs text-gray-500 hover:text-gray-300 text-center py-1">
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- OTP code entry step ---
  if (phoneStep === 'code_sent') {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Fantasy Futbol" className="w-28 h-28 object-contain" />
          </div>
          <h2 className="text-xl font-heading font-bold text-accent-light-blue mb-2 text-center">Enter your code</h2>
          <p className="text-sm text-gray-400 text-center mb-6">
            We sent a code to {normalizePhoneNumberFE(phone)}. New here? Verifying creates your account.{' '}
            <button type="button" onClick={() => { setPhoneStep('idle'); setError(null); setCode(''); }} className="text-accent-light-blue hover:text-accent-yellow underline text-xs">
              Change
            </button>
          </p>
          {errorBanner}
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              placeholder="6-digit code"
              disabled={isLoadingVerify}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50 text-center text-xl tracking-widest"
            />
            <button
              type="submit"
              disabled={isLoadingVerify}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingVerify ? 'bg-accent-yellow text-primary animate-pulse cursor-not-allowed' : 'bg-secondary hover:bg-red-700 text-white'}`}
            >
              {isLoadingVerify ? 'Verifying...' : 'VERIFY & CONTINUE'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Main screen ---
  return (
    <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
      <SEO
        title="Log In | Fantasy Futbol"
        description="Log in to Fantasy Futbol to play daily and season-long fantasy soccer for the Premier League and La Liga."
        path="/login"
        noindex
      />
      <div className="w-full max-w-sm space-y-6">

        {/* Logo + tagline */}
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="Fantasy Futbol" className="w-36 h-36 object-contain" />
          <p className="text-yellow-500 font-bold text-sm text-center">Fantasy Football for Soccer</p>
        </div>

        <div className="bg-surface-card p-8 rounded-lg shadow-xl space-y-3">
          {sessionExpired && (
            <p className="text-sm text-center rounded p-3 text-accent-light-blue bg-blue-950/50 border border-accent-light-blue/50">
              Your session expired. Sign in again and we&apos;ll return you to where you left off.
            </p>
          )}
          {errorBanner}

          {/* Google */}
          <GoogleAuthButton
            mode="signin"
            isLoading={isLoadingGoogle}
            onSuccess={handleGoogleSuccess}
            onError={(msg) => setError(msg)}
          />

          {/* Apple */}
          <button
            type="button"
            onClick={handleAppleClick}
            disabled={isLoadingApple}
            aria-label="Sign in with Apple"
            className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-base bg-black text-white hover:bg-gray-900 transition-colors ${isLoadingApple ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path d="M17.05 13.04c-.03-2.95 2.4-4.37 2.51-4.44-1.37-2-3.5-2.28-4.26-2.31-1.81-.18-3.54 1.07-4.46 1.07-.93 0-2.35-1.05-3.86-1.02-1.99.03-3.82 1.15-4.84 2.93-2.06 3.57-.53 8.86 1.49 11.76.98 1.42 2.15 3.02 3.69 2.96 1.48-.06 2.04-.96 3.83-.96 1.78 0 2.29.96 3.86.93 1.59-.03 2.6-1.45 3.58-2.88 1.13-1.65 1.6-3.26 1.62-3.34-.03-.01-3.1-1.19-3.16-4.7zM14.46 4.5c.81-.99 1.36-2.36 1.21-3.73-1.17.05-2.59.78-3.43 1.76-.75.87-1.41 2.27-1.24 3.6 1.3.1 2.65-.66 3.46-1.63z" />
            </svg>
            <span>{isLoadingApple ? 'Loading...' : 'Continue with Apple'}</span>
          </button>

          {/* Phone */}
          <form onSubmit={handleRequestCode} className="space-y-3">
            <input
              type="tel"
              inputMode="tel"
              id="phone-login"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="Phone number"
              disabled={isLoadingOtp}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoadingOtp}
              className={`w-full py-2.5 px-4 rounded-lg font-heading font-bold text-base transition-colors duration-200 border ${isLoadingOtp ? 'border-gray-600 text-gray-500 animate-pulse cursor-not-allowed' : 'border-gray-500 text-gray-300 hover:border-accent-light-blue hover:text-accent-light-blue'}`}
            >
              {isLoadingOtp ? 'Sending Code...' : 'Continue with Phone'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-600">
          Need help?{' '}
          <a href="mailto:support@fantasy.futbol" className="text-gray-500 hover:text-accent-light-blue underline">support@fantasy.futbol</a>
        </p>

        <p className="text-center text-xs text-gray-600 mt-2">
          <Link
            to="/privacy"
            state={{ from: location.pathname + location.search }}
            className="text-gray-500 hover:text-accent-light-blue underline"
          >
            Privacy Policy
          </Link>
          <span className="mx-2">·</span>
          <Link
            to="/support"
            state={{ from: location.pathname + location.search }}
            className="text-gray-500 hover:text-accent-light-blue underline"
          >
            Support
          </Link>
        </p>

      </div>
    </div>
  );
};

export default LoginPage;
